import { useCallback } from 'react'
import { useMutation } from '@apollo/client/react'
import { AdminAPI, GraphQL } from '@guestgoodie/graphql'

import * as Form from '@/features/form'

export type FormData = {
  state: GraphQL.StayStates
  scheduledStart: Date
  scheduledStop: Date
  guestIds: number[]
  spaceId?: number | null
}

interface Options {
  id?: number
  onDone?: () => void
}

export default function useStayForm(options: Options) {
  const { id, onDone } = options

  const [createStay] = useMutation(AdminAPI.stay.CREATE, {
    refetchQueries: [AdminAPI.stay.QUERY, AdminAPI.stay.GET]
  })

  const [updateStay] = useMutation(AdminAPI.stay.UPDATE, {
    update(cache, { data }) {
      if (!data) return

      // Write the raw mutation result to the GET query cache
      if (id) {
        cache.writeQuery({
          query: AdminAPI.stay.GET,
          variables: {
            id,
            withSpace: true,
            withGuests: true
          },
          data: {
            getStay: data.updateStay
          }
        })
      }
    }
  })

  const { errors, handlePromiseCatch } = Form.useErrorHelpers<FormData>({})

  const create = useCallback(
    (data: FormData) => {
      return createStay({
        variables: {
          input: data
        }
      })
        .then(({ data, error }) => {
          if (error) return Promise.reject(error)
          if (!data) return Promise.reject('unknown')

          onDone?.()
        })
        .catch(error => handlePromiseCatch(error))
    },
    [onDone, createStay, handlePromiseCatch]
  )

  const update = useCallback(
    async (data: FormData) => {
      if (!id) return

      return updateStay({
        variables: {
          input: {
            ...data,
            id
          },
          withSpace: true,
          withGuests: true
        }
      })
        .then(({ data, error }) => {
          if (error) return Promise.reject(error)
          if (!data) return Promise.reject('unknown')

          onDone?.()
        })
        .catch(error => handlePromiseCatch(error))
    },
    [id, onDone, updateStay, handlePromiseCatch]
  )

  return {
    errors,
    create,
    update
  }
}
